import { isEmpty } from "lodash";
import * as React from "react";

import { isUserRestricted } from "@app/authorization";
import { Unauthorized } from "@app/shared/ErrorPages";
import { QESAlert, QESButton } from "@app/shared/qes-standards";
import { Roles, UserInfo, UserService } from "@app/user";
import { JSX } from "react";

interface ProfileProps {
  user: UserInfo;
}

interface ProfileState {
  /** Whether or not the profile is valid and OK to submit */
  isProfileValid: boolean;
  /** A copy of the current user */
  user: UserInfo;
  /** Any errors that may have occurred as a result of submitting the form */
  errors?: string;
}

class Profile extends React.Component<ProfileProps, ProfileState> {
  /** Whether not the current user is a token user */
  private readonly isTokenUser: boolean;
  private readonly userService = new UserService();
  private readonly validation = new ProfileValidation();

  constructor(props: ProfileProps) {
    super(props);

    this.isTokenUser = isUserRestricted([], [Roles.TOKEN_USER], this.props.user);
    const user = !this.props.user
      ? undefined
      : { ...this.props.user };

    this.state = {
      isProfileValid: this.validation.validate(user),
      user,
    };
  }

  public render() {
    const encodedReturnUrl = encodeURIComponent(window.location.href);

    return !this.state.user
      ? (<Unauthorized />)
      : (
        <form id="account-manage-profile-form">

          {this.renderErrors()}

          <div className="form-group row">
            <label className="col-sm-2 col-form-label control-label">Username</label>
            <div className="col-sm-8">
              <input type="text" className="form-control" placeholder="Username" disabled={true} value={this.state.user.name} />
            </div>
            <div className="col-sm-1 ptb6">
              <a className="qes-button qes-button-secondary" type="button" href={`${window.authEndpoint}/app/account/change-username?returnUrl=${encodedReturnUrl}`}>
                <span className="d-none d-sm-block"><i className="fa fa-pencil" /></span>
                <span className="d-block d-sm-none">Change</span>
              </a>
            </div>
          </div>

          <div className="form-group row">
            <label className="col-sm-2 col-form-label control-label">Email</label>
            <div className="col-sm-8">
              <input type="text" className="form-control" placeholder="Email" disabled={true} value={this.state.user.email} />
            </div>
            <div className="col-sm-1 ptb6">
              <a className="qes-button qes-button-secondary" type="button" href={`${window.authEndpoint}/app/account/change-email?returnUrl=${encodedReturnUrl}`}>
                <span className="d-none d-sm-block"><i className="fa fa-pencil" /></span>
                <span className="d-block d-sm-none">Change</span>
              </a>
            </div>
          </div>

          <div className={this.getFormGroupClassesFor(["firstName", "middleInitial", "lastName"])}>
            <label className="col-sm-2 col-form-label d-none d-sm-block control-label" htmlFor="firstName">Name</label>
            <label className="col-sm-2 col-form-label d-block d-sm-none control-label" htmlFor="firstName">First Name</label>
            <div className="col-sm-3">
              {this.inputFor("firstName", { placeholder: "First" })}
            </div>

            <label className="col-sm-2 col-form-label d-block d-sm-none control-label mt-3" htmlFor="middleInitial">Middle Initial</label>
            <div className="col-sm-2">
              {this.inputFor("middleInitial", { placeholder: "MI" })}
            </div>

            <label className="col-sm-2 col-form-label d-block d-sm-none control-label mt-3" htmlFor="User_LastName">Last Name</label>
            <div className="col-sm-3">
              {this.inputFor("lastName", { placeholder: "Last" })}
            </div>
          </div>

          <div className={this.getFormGroupClassesFor(["address"])}>
            <label className="col-sm-2 col-form-label control-label" htmlFor="User_Address">Address</label>
            <div className="col-sm-8">
              {this.inputFor("address")}
            </div>
          </div>

          <div className={this.getFormGroupClassesFor(["city"])}>
            <label className="col-sm-2 col-form-label d-block d-sm-none control-label" htmlFor="User_City">City</label>
            <div className="col-sm-8 offset-sm-2">
              {this.inputFor("city")}
            </div>
          </div>

          <div className={this.getFormGroupClassesFor(["state", "zip"])}>
            <label className="col-sm-2 col-form-label d-block d-sm-none control-label" htmlFor="User_State">State</label>
            <div className="col-sm-4 offset-sm-2">
              {this.inputFor("state")}
            </div>

            <label className="col-sm-2 col-form-label d-block d-sm-none control-label mt-3" htmlFor="User_Zip">Zip</label>
            <div className="col-sm-4">
              {this.inputFor("zip")}
            </div>
          </div>

          <div className={this.getFormGroupClassesFor(["phoneNumber"])}>
            <label className="col-sm-2 col-form-label control-label" htmlFor="User_PhoneNumber">Phone</label>
            <div className="col-sm-8">
              {this.inputFor("phoneNumber", { placeholder: "Phone #" })}
            </div>
          </div>

          <div className="form-group row">
            <div className="offset-sm-2 col-sm-8">
              <div className="float-right">
                <QESButton qesTestId="profile_update" qesType="primary" disabled={!this.state.isProfileValid} onClick={this.handleUpdate}>Update</QESButton>
              </div>
            </div>
          </div>

        </form>
      );
  }

  public renderErrors(): JSX.Element {
    return !this.state.errors
      ? (<></>)
      : (
        <div className="row">
          <div className="col-12">
            <QESAlert type="danger" persist={false} onClose={() => this.setState({ errors: undefined })}>
              One or more validation errors occurred.
            </QESAlert>
          </div>
        </div>
      );
  }

  /**
   * Returns the classes for form-groups requiring validation
   * @param fields
   */
  private getFormGroupClassesFor = (fields: Array<keyof UserInfo>): string => {
    return `form-group row${this.validation.areFieldsValid(fields, this.state.user) ? "" : " has-error"}`;
  }

  /**
   * Binds the input change to the state based on target id (which needs to be the property name of the state item)
   * @param e
   */
  private handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const user = {
      ...this.state.user,
      [e.target.id]: e.target.value,
    };
    const isProfileValid = this.validation.validate(user);

    this.setState({
      isProfileValid,
      user,
    });
  }

  /**
   * Handles the update of the form and any resulting errors or successes
   * @param e
   */
  private handleUpdate = async (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();

    try {
      await this.userService.updateUserProfile(this.state.user);
      window.location.reload();
    } catch (e) {
      //TODO: use e.responseJSON.errors to formulate more descriptive validation errors
      this.setState({
        errors: e.responseJSON.title,
      });
    }
  }

  /**
   * Creates an input element for the given user property
   * @param property
   * @param inputProps
   */
  private inputFor(property: keyof UserInfo, inputProps?: React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>): JSX.Element {
    const defaultValue = this.state.user[property]
      ? this.state.user[property].toString()
      : "";
    const placeholder = `${property.charAt(0).toUpperCase()}${property.slice(1)}`;
    const name = `User.${placeholder}`;

    const { className, ...rest } = inputProps
      ? inputProps
      : {} as React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

    return (
      <input
        className={`form-control ${className}`}
        defaultValue={defaultValue}
        id={property}
        name={name}
        onChange={this.handleInputChange}
        placeholder={placeholder}
        type="text"
        {...rest}
      />
    );
  }
}

class ProfileValidation {
  /** The User fields required so that the profile can be considered valid. */
  private readonly requiredFields: Array<keyof UserInfo> = ["address", "city", "firstName", "lastName", "phoneNumber", "state", "zip"];

  /** Determines whether the given field is valid for the given user. */
  public isFieldValid(field: keyof UserInfo, user: UserInfo): boolean {
    if (!user || !field) {
      return false;
    }

    if (!this.requiredFields.includes(field)) {
      return true;
    }

    return user[field]
      ? !isEmpty(user[field].toString())
      : false;
  }

  /** Determines whether all of the given fields are valid for the given user. */
  public areFieldsValid(fields: Array<keyof UserInfo>, user: UserInfo): boolean {
    if (!user || !fields || fields.length < 1) {
      return false;
    }

    return fields.every((f) => this.isFieldValid(f, user));
  }

  /** Validates the given user and returns whether it valid or not */
  public validate(user: UserInfo): boolean {
    return this.requiredFields.every((f) => this.isFieldValid(f, user));
  }
}

export {
  Profile,
};
